import React, { ReactNode, useRef, useState } from "react";
import type * as controls from "../PhoenixFormsV4.Controls";
import {
  DatePicker,
  TimePicker,
  TextField
} from "react-md";
import PNXLabel from "./PNXLabel";
import { PNXTag } from "./PhoenixPhotoTagGroup";


const datePickerToISO = (str: string) => {
  if (!str) return "";

  const sstr = str.split("/");

  let month = sstr[0];
  let day = sstr[1];
  const year = sstr[2];

  if (month.length === 1) month = "0" + month;
  if (day.length === 1) day = "0" + day;

  return `${year}-${month}-${day}`;
};

const dateISOToPicker = (str: string) => {
  if (!str) return null;

  const sstr = str.split("-");

  return `${sstr[1]}/${sstr[2]}/${sstr[0]}`;
};

export const timeISOToPicker = (str: string): Date => {
  if (!str) return null;

  let hour = 0;
  let minute = 0;

  const timeAmPmSplit = str.split(" ");
  if (timeAmPmSplit[1] === "PM")
    hour += 12;

  const hourMinuteSplit = timeAmPmSplit[0].split(":");
  hour += parseInt(hourMinuteSplit[0]);
  minute += parseInt(hourMinuteSplit[1]);

  const pickerDate = new Date();
  pickerDate.setHours(hour, minute, 0, 0);
  return pickerDate;
};

const timePickerToISO = (date: Date) => {
  if (!date) return "";

  let hour = `${date.getHours()}`;
  let minute = `${date.getMinutes()}`;

  if (hour.length === 1) hour = "0" + hour;
  if (minute.length === 1) minute = "0" + minute;

  return `${hour}:${minute}:00`;
};

interface Props {
  controlProps: controls.FormControl;
  updateValue: (newValue: string) => void;
  onControlBlur: () => void;
};

const PhoenixInput = ({
  controlProps,
  updateValue,
  onControlBlur,
} : Props) => {

  const id = `phoenixinput-${controlProps.id}`;

  // integer, decimal, and default text need to only trigger when value has changed and control loses focus (i.e. same as native html onChange event)
  const [currentValue, setCurrentValue] = useState<string>(controlProps.value ?? "");
  const lastValue = useRef<string>(controlProps.value ?? "");
  const blurUpdate = () => {
    if (lastValue.current !== currentValue) {
      lastValue.current = currentValue;
      updateValue(currentValue);
      onControlBlur();
    }
  }

  const controlledUpdate = (newValue: string) => {
    switch (controlProps.dataType) {
      case "integer":
        if (!/^-?[0-9]*$/g.test(newValue)) {
          setCurrentValue(newValue);
          return;
        }
        break;
      case "decimal":
        if (!/^-?([0-9]*)(\.)?([0-9]+)?$/g.test(newValue)){
          setCurrentValue(newValue);
          return;
        }
        break;
    }

    setCurrentValue(newValue);
  };  

  let inputField: ReactNode = null;

  switch (controlProps.dataType) {
    case "integer":
    case "decimal":
      inputField = (
        <TextField
          id={id}
          onChange={(newValue) => { controlledUpdate(`${newValue}`); }}
          onBlur={blurUpdate}
          value={currentValue}
          //type="number" // default browser event behavior of actual number fields is kind of terrible. If we need numeric keyboard on mobile I suggest modifying the input with a ref and https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
          placeholder="Enter Number..."
        />
      );
      break;
    case "date": // xml: yyyy-mm-dd  display: mm/dd/yyyy
      // these ranges were put in for SD-25563 - the issue is that for date of birth fields, you had to open the date picker twice in order to go back more than 50 years. this allows us to select a wider range without reopening the picker
      // if these years are too large or too small, feel free to adjust, they were just random guidelines picked by jordan and dubie
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 100);
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 50);

      inputField = (
        <DatePicker
          id={id}
          autoOk
          onChange={(dateString, dateObject) => { updateValue(datePickerToISO(dateString)); }}
          onBlur={() => { onControlBlur(); }}
          defaultValue={dateISOToPicker(controlProps.value)}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    case "time": // xml: hh:mm:ss    display: hh:mm am/pm
      inputField = (
        <TimePicker
          id={id}
          autoOk
          onChange={(timeString, dateObject) => { updateValue(timePickerToISO(dateObject)); }}
          onBlur={() => { onControlBlur(); }}
          defaultValue={timeISOToPicker(controlProps.value)}
        />
      );
      break;
    default:
      inputField = (
        <TextField
          id={id}
          onChange={(newValue) => { controlledUpdate(`${newValue}`); }}
          onBlur={blurUpdate}
          value={currentValue}
          placeholder="Enter Value..."
        />
      );
      break;
  }

  
  
  return (
    <div className={`data-type-${controlProps.dataType}`}>
      <PNXLabel controlType="label" htmlFor={id} helpTopicId={controlProps.topicId}>{controlProps.label}</PNXLabel>
      <div className="phoenixinput-and-tag">
        {inputField}
        {controlProps.hint && <PNXTag tag={controlProps.hint} />}
      </div>
    </div>
  );
};

export default PhoenixInput;